import {
  CnpjFormField,
  CpfFormField,
  FloatingFormItem,
  InputText,
} from '@/components/_common';
import {
  IntermediaryIndicationType,
  InvoiceGeneralFormValues,
  TypeOfTaxpayers,
} from '@/core/domain/invoice/invoice/Invoice.types';
import {
  Checkbox,
  Col,
  Descriptions,
  Form,
  FormInstance,
  Input,
  Radio,
  Row,
  Select,
  Typography,
} from 'antd';
import { useWatch } from 'antd/lib/form/Form';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import SelectRecipientType from './SelectRecipientType';
import {
  InvoiceTaxpayerType,
  InvoiceType,
} from '@/core/domain/invoice/invoices/invoices.enums';
import RadioGroup from '@/components/_common/RadioGroup';
import { useInvoiceFormContext } from '../../../InvoiceFormProvider';
import { Link } from 'react-router-dom';
import { useCustomerDetailReduced } from '@/core/domain/people/Customers/Customers.hooks';
import StatusTag from '@/components/_common/StatusTag';
import { useMatrixCustomerSettingsDetails } from '@/core/domain/enterprise/MatrixCustomerSettings/MatrixCustomerSettings.hooks';

interface Props {
  isEdit?: boolean;
  selectedInvoiceType?: InvoiceType;
  form: FormInstance<InvoiceGeneralFormValues>;
}

const RecipientForm = ({ form, isEdit, selectedInvoiceType }: Props) => {
  const intl = useIntl().formatMessage;
  const { Title } = Typography;

  const {
    disabledFields,
    invoice: { data: invoice },
  } = useInvoiceFormContext();

  const { data: customer } = useCustomerDetailReduced(invoice?.customerId);
  const { data: customerMatrixSettings } = useMatrixCustomerSettingsDetails();

  const { recipient } = form.getFieldsValue();

  const recipientType = Form.useWatch(['recipient', 'recipientType'], form);
  const fantasyName = useWatch(['recipient', 'fantasyName'], form);
  const customerAddress = useWatch(['recipient', 'customerAddress'], form);
  const cityName = useWatch(['recipient', 'cityName'], form);

  const intermediaryIndication = useWatch('intermediaryIndication', form);
  const intermediaryIndicationNfs = useWatch('intermediaryIndicationNfs', form);

  const intermediaryIndicationType = useWatch(
    ['recipient', 'intermediaryIndicationType'],
    form,
  );

  useEffect(() => {
    if (`${recipientType}` === 'CUSTOMER' && isEdit) {
      form.setFieldsValue({
        recipient: {
          taxpayerType:
            invoice?.taxpayerType || InvoiceTaxpayerType.NON_CONTRIBUTOR,
        },
      });
    }
  }, [recipientType]);

  const typeOfTaxpayersOptions = [
    {
      label: intl({
        id: 'invoice.nfe.recipient.form.typeOfTaxpayers.contributorIcms',
      }),
      value: TypeOfTaxpayers.icmsTaxPayer,
    },
    {
      label: intl({
        id: 'invoice.nfe.recipient.form.typeOfTaxpayers.contributorExempt',
      }),
      value: TypeOfTaxpayers.exemptTaxPayer,
    },
    {
      label: intl({
        id: 'invoice.nfe.recipient.form.typeOfTaxpayers.nonTaxpayer',
      }),
      value: TypeOfTaxpayers.nonContributor,
    },
  ];

  const redirect = () => {
    if (recipient?.customerId) {
      return `/app/customers/${recipient.customerId}/general`;
    }

    if (recipient?.supplierId) {
      return `/app/registry/suppliers/${recipient.supplierId}/general`;
    }

    if (recipient?.insurerId) {
      return `/app/registry/insurers/${recipient.insurerId}/general`;
    }

    return '';
  };

  return (
    <Form form={form} name="invoiceRecipient">
      <Row gutter={[16, 16]}>
        <SelectRecipientType
          form={form}
          isEdit={isEdit}
          disabledFields={disabledFields}
          invoiceTypeEdit={selectedInvoiceType}
        />

        {selectedInvoiceType !== InvoiceType.NFS && (
          <>
            <Col xs={24} md={12} xl={6}>
              <FloatingFormItem
                label={intl({
                  id: 'invoice.nfe.recipient.form.typeOfTaxpayers',
                })}
                name={['recipient', 'taxpayerType']}
              >
                <Select
                  options={typeOfTaxpayersOptions}
                  disabled={disabledFields}
                  allowClear={selectedInvoiceType === InvoiceType.NFC}
                />
              </FloatingFormItem>
            </Col>

            <Col span={24}>
              <Row gutter={[24, 24]}>
                {`${intermediaryIndication}` === 'false' ||
                intermediaryIndication === undefined ? null : (
                  <>
                    <Col span={24}>
                      <Title level={5}>
                        {intl({ id: 'invoice.nfe.recipient.form.title' })}
                      </Title>
                    </Col>

                    <Col span={24}>
                      <Row gutter={[24, 24]}>
                        <Col xs={24} md={12} xl={8}>
                          <CnpjFormField
                            required={false}
                            fieldName={['recipient', 'intermediaryCpfCnpj']}
                            label={intl({
                              id: 'invoice.nfe.recipient.form.intermediaryCnpj',
                            })}
                            disabled={disabledFields}
                          />
                        </Col>

                        <Col xs={24} md={12} xl={8}>
                          <FloatingFormItem
                            label={intl({
                              id: 'invoice.nfe.recipient.form.SellerId',
                            })}
                            name="registrationIdentifier"
                          >
                            <Input disabled={disabledFields} />
                          </FloatingFormItem>
                        </Col>
                      </Row>
                    </Col>
                  </>
                )}
              </Row>
            </Col>
          </>
        )}

        <Col span={24}>
          <Descriptions
            bordered
            layout="vertical"
            labelStyle={{ fontWeight: 'bold' }}
            size="small"
            column={{ lg: 16, xs: 4, md: 12, sm: 8, xl: 24, xxl: 24 }}
            style={{ marginBottom: 24 }}
          >
            <Descriptions.Item label={'Nome fantasia'} span={8}>
              {fantasyName ? (
                <Link
                  to={redirect()}
                  target="_blank"
                  style={{ textDecoration: 'underline' }}
                >
                  {fantasyName}
                </Link>
              ) : (
                ''
              )}
            </Descriptions.Item>

            {!!customer?.category?.id &&
              customerMatrixSettings?.showCategoryInInvoice && (
                <Descriptions.Item span={8} label="Categoria">
                  <StatusTag
                    color={customer?.category.color}
                    label={customer?.category.name}
                    title={customer?.category.name}
                    size="md"
                    style={{ width: 400, display: 'block' }}
                  />
                </Descriptions.Item>
              )}

            <Descriptions.Item label={'Endereço'} span={8}>
              {customerAddress ?? ''}
            </Descriptions.Item>

            <Descriptions.Item label={'Cidade'} span={8}>
              {cityName ?? ''}
            </Descriptions.Item>
          </Descriptions>
        </Col>

        {selectedInvoiceType === InvoiceType.NFS && (
          <Col span={24}>
            <FloatingFormItem
              name={'intermediaryIndicationNfs'}
              valuePropName="checked"
              initialValue={false}
            >
              <Checkbox>Informar Intermediador</Checkbox>
            </FloatingFormItem>
          </Col>
        )}

        {selectedInvoiceType === InvoiceType.NFS && intermediaryIndicationNfs && (
          <>
            <Col span={24}>
              <Form.Item
                noStyle
                name={['recipient', 'intermediaryIndicationType']}
                initialValue={IntermediaryIndicationType.cpf}
                getValueFromEvent={({ target: { value } }) => {
                  form.setFieldValue(
                    ['recipient', 'intermediaryCpfCnpj'],
                    undefined,
                  );
                  return value;
                }}
              >
                <RadioGroup style={{ marginBottom: '8px' }}>
                  <Radio value={IntermediaryIndicationType.cpf}>
                    Pessoa Física
                  </Radio>
                  <Radio value={IntermediaryIndicationType.cnpj}>
                    Pessoa Juridica
                  </Radio>
                </RadioGroup>
              </Form.Item>
            </Col>

            <Col span={24} md={12} xl={6}>
              {intermediaryIndicationType === IntermediaryIndicationType.cpf ? (
                <CpfFormField
                  fieldName={['recipient', 'intermediaryCpfCnpj']}
                />
              ) : (
                <CnpjFormField
                  fieldName={['recipient', 'intermediaryCpfCnpj']}
                />
              )}
            </Col>

            <Col span={24} md={12} xl={6}>
              <FloatingFormItem
                name={['recipient', 'intermediaryCompanyName']}
                label="Nome do intermediador"
              >
                <Input />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} xl={6}>
              <FloatingFormItem
                name={['recipient', 'intermediaryMunicipalRegistration']}
                label="Inscrição Municipal"
              >
                <Input />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} xl={6}>
              <FloatingFormItem
                name={['recipient', 'intermediaryCityCode']}
                label="Codigo IBGE da cidade"
              >
                <InputText onlyNumber maxLength={7} />
              </FloatingFormItem>
            </Col>
          </>
        )}
      </Row>
    </Form>
  );
};

export default RecipientForm;
